import React, { useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import FixedButton from 'src/components/form-components/FixedButton/FixedButton';
import JsApiContext from 'src/contexts/JsApi/JsApiContext';
import { IJsApiQuestion, IQuestion } from 'src/entities/question';
import { reservationValueChanged } from 'src/entities/reservation';
import { getCustomParams } from 'src/services/api/service';
import makeCancelable from 'src/services/cancelablePromise';
import FormValidator from 'src/services/formValidator';
import { StepType } from 'src/services/getStepOrder';
import globalMessages from 'src/services/globalMessages';
import { createValidationRules } from 'src/services/validations/validations';
import { AppLocale, IStepModuleProps } from '../../../../models/modules';
import { toAnswers, toCustomerData } from '../helpers';
import CustomerData, { ICustomerData } from './CustomerData';

const CustomerDataStep: React.FC<IStepModuleProps> = ({
    isActive,
    data,
    onLoadingChanged,
    onComplete,
}) => {
    const intl = useIntl();
    const jsApiContext = useContext(JsApiContext);
    const prevProps = useRef({ data });
    const customerDataStepValue = data[StepType.CustomerData];
    const initialAnswers = customerDataStepValue
        ? toCustomerData(customerDataStepValue.answers)
        : {};
    const [validator, setValidator] = useState<FormValidator | undefined>();
    const [answers, setAnswers] = useState<ICustomerData>(initialAnswers);
    const [questions, setQuestions] = useState<IQuestion[]>([]);
    const [selectedArriveTime, setSelectedArriveTime] = useState(
        customerDataStepValue?.arriveTime || ''
    );
    const [shouldValidate, setShouldValidate] = useState(false);
    const [hasQuestionsLoaded, setHasQuestionsLoaded] = useState(false);
    const [prevHasQuestionsLoaded, setPrevHasQuestionsLoaded] = useState(false);
    const validation = validator
        ? shouldValidate
            ? validator.validate(answers)
            : validator.getInitial()
        : undefined;

    const cleanAnswers = (currentAnswers: ICustomerData, currentQuestions: IQuestion[]) => {
        return Object.entries(currentAnswers).reduce((acc, entry) => {
            if (currentQuestions.some((q) => q.Id === +entry[0])) {
                acc[+entry[0]] = entry[1];
                jsApiContext.setQuestionValue(+entry[0], entry[1]);
            } else {
                jsApiContext.cleanQuestionValue(+entry[0]);
            }
            return acc;
        }, {} as ICustomerData);
    };

    const fetchQuestions = () => {
        const service = data[StepType.Service] || data[StepType.Timetable]?.service;

        if (service) {
            const questionsSubscription = makeCancelable<IQuestion[]>(getCustomParams(service.Id));
            questionsSubscription.promise
                .then((result) => {
                    const contextDataMap = jsApiContext?.data
                        ? jsApiContext.data.reduce((acc, question) => {
                              acc[question.Id] = question;
                              return acc;
                          }, {} as Record<number, IJsApiQuestion>)
                        : {};

                    const mergedQuestions = result
                        .filter((question) => result.some((q) => q.Id === question.Id))
                        .map((question) => contextDataMap[question.Id] || question);

                    jsApiContext.setData(mergedQuestions);

                    setQuestions(mergedQuestions); // setting merged questions to state
                    setAnswers(cleanAnswers(answers, mergedQuestions));
                    setHasQuestionsLoaded(true);

                    const event = new CustomEvent('new-questions', { detail: mergedQuestions });
                    window.dispatchEvent(event);
                })
                .catch(() => null);
            return questionsSubscription;
        }
        return undefined;
    };

    const setValidatorByQuestions = (value: IQuestion[]) => {
        setValidator(new FormValidator(createValidationRules(value, intl.locale as AppLocale)));
    };

    const handleNext = () => {
        setShouldValidate(true);
    };

    const handleChange = (question: IQuestion, currentAnswers: ICustomerData) => {
        const event = new CustomEvent('new-valueChanged', {
            detail: { question, answer: currentAnswers[question.Id] },
        });
        window.dispatchEvent(event);

        jsApiContext.setQuestionValue(question.Id, currentAnswers[question.Id]);

        setAnswers(currentAnswers);
        setShouldValidate(false);
    };

    const handleArriveTimeChange = (arriveTime: string) => {
        setSelectedArriveTime(arriveTime);
    };

    useEffect(() => {
        const questionsSubscription = fetchQuestions();

        return () => {
            if (questionsSubscription) {
                questionsSubscription.cancel();
            }
        };
    }, []);

    useEffect(() => {
        if (reservationValueChanged(data, prevProps.current.data, 'service')) {
            fetchQuestions();
        }

        if (prevHasQuestionsLoaded !== hasQuestionsLoaded) {
            onLoadingChanged?.(!hasQuestionsLoaded);
        }

        if (jsApiContext.data.length > 0) {
            setValidatorByQuestions(jsApiContext.data);
        }

        prevProps.current = { data };
        setPrevHasQuestionsLoaded(hasQuestionsLoaded);
    }, [data, hasQuestionsLoaded, jsApiContext.data]);

    useEffect(() => {
        if (hasQuestionsLoaded) {setValidatorByQuestions(questions);}
    }, [hasQuestionsLoaded]);

    useEffect(() => {
        if (shouldValidate && validator?.validate(answers).isValid) {
            const displayValue = intl.formatMessage(globalMessages.nAnswers, {
                n: Object.values(answers).filter((a) => !!a).length,
            });
            onComplete({
                ...data,
                [StepType.CustomerData]: {
                    answers: toAnswers(questions, answers),
                    displayValue,
                    arriveTime: selectedArriveTime,
                },
            });
        }
    }, [shouldValidate]);

    return (
        <div className="customer-data-step">
            <CustomerData
                questions={jsApiContext.data}
                calendar={data[StepType.Calendar]}
                service={data[StepType.Service] || data[StepType.Timetable]?.service}
                reservationStart={
                    data[StepType.Date]?.startTime || data[StepType.Timetable]?.schedule.Start
                }
                answers={answers}
                validation={validation}
                selectedArriveTime={selectedArriveTime}
                onChange={handleChange}
                onArriveTimeChange={handleArriveTimeChange}
                hasLoaded={hasQuestionsLoaded}
            />
            {isActive && (
                <FixedButton
                    className="customer-data-step__button"
                    caption={globalMessages.next}
                    onClick={handleNext}
                />
            )}
        </div>
    );
};

export default CustomerDataStep;
